import * as React from 'react';
import {FunctionComponent} from 'react';
import {PartialWorkout} from '../../../Models/PartialWorkout';
import {DateRange} from '../../../Domain/DateRange';
import {differenceInDays, differenceInMonths} from 'date-fns';
import {YearlySummaryBarChart} from './SummaryBarChart/YearlySummaryBarChart';
import {MonthlySummaryBarChart} from './SummaryBarChart/MonthlySummaryBarChart';
import {WeeklySummaryBarChart} from './SummaryBarChart/WeeklySummaryBarChart';
import {DailySummaryBarChart} from './SummaryBarChart/DailySummaryBarChart';

interface OwnProps {
    partialWorkouts: PartialWorkout[];

    //Grouping will follow the daterange if supplied, otherwise a daterange will be created from the partialworkouts
    dateRange?: DateRange;
}

function dateRangeFromPartialWorkouts(partialWorkouts: PartialWorkout[]): DateRange {
    if (partialWorkouts.length === 0) {
        return {
            from: new Date(),
            till: new Date(),
        };
    }

    return {
        from: partialWorkouts.reduce((oldest: Date | null, partialWorkout) => {
            return oldest !== null && oldest < partialWorkout.executed_at ? oldest : partialWorkout.executed_at;
        }, null),
        //We use the current date as the till date, as we want to show the current month in the chart
        till: new Date(),
    };
}

function mergeDateRanges(suppliedDateRange: DateRange | undefined, calculatedDateRange: DateRange): DateRange {
    const result = suppliedDateRange ?? calculatedDateRange;
    if (result.from === null) {
        result.from = calculatedDateRange.from;
    }
    if (result.till === null) {
        result.till = calculatedDateRange.till;
    }

    return result;
}

export const SummaryBarChart: FunctionComponent<OwnProps> = ({dateRange, partialWorkouts}) => {
    if (partialWorkouts.length === 0) {
        return null;
    }

    const calculatedDateRange = dateRangeFromPartialWorkouts(partialWorkouts);
    const mergedDateRange = mergeDateRanges(dateRange, calculatedDateRange);

    if (mergedDateRange.from === null || mergedDateRange.till === null) {
        return null;
    }

    if (differenceInMonths(mergedDateRange.till, mergedDateRange.from) >= 24) {
        return <YearlySummaryBarChart dateRange={mergedDateRange} partialWorkouts={partialWorkouts} />;
    }

    if (differenceInMonths(mergedDateRange.till, mergedDateRange.from) > 3) {
        return <MonthlySummaryBarChart dateRange={mergedDateRange} partialWorkouts={partialWorkouts} />;
    }

    if (differenceInDays(mergedDateRange.till, mergedDateRange.from) > 7) {
        return <WeeklySummaryBarChart dateRange={mergedDateRange} partialWorkouts={partialWorkouts} />;
    }

    return <DailySummaryBarChart dateRange={mergedDateRange} partialWorkouts={partialWorkouts} />;
};
